// Live export preview.
//
// Renders the resolved section blocks (real data, from exportData.js) in the
// visual idiom of the chosen format, so the customer sees exactly what they
// are assembling before they download. Block renderers are shared across the
// PDF (paper) and PowerPoint (slide) previews; CSV and Excel get their own
// data-shaped renderers.
//
// Calculations: none. Tables are capped to a handful of rows for the preview
// with a "+N more" note; the generated file follows the same contract.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { generateCsv } from '../exportGenerators';

const PREVIEW_ROWS = 6;

// -- Shared block renderers -------------------------------------------------
function BlockGauge({ block }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: 48, fontWeight: 800, color: 'error.main', lineHeight: 1, fontFamily: 'system-ui' }}>{block.value}</Typography>
            <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{block.band}</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{block.sub}</Typography>
            </Box>
        </Box>
    );
}

function BlockMetrics({ block }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {block.items.map((m) => (
                <Box key={m.label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary', lineHeight: 1, fontFamily: 'system-ui' }}>{m.value}</Typography>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.primary', mt: 0.25 }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>{m.sub}</Typography>
                </Box>
            ))}
        </Box>
    );
}

// Bars match the portal's .brow/.btrack/.bfill style: 7px track, 3px radius,
// var(--bar-track) track and var(--teal) fill (auto theme-aware via the tokens
// on .rbx-root), muted label and dim right-aligned value.
function BlockBars({ block }) {
    const max = Math.max(1, ...block.items.map((b) => (b.max != null ? b.max : Number(b.value) || 0)));
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {block.items.map((b) => {
                const wpct = Math.max(0, Math.min(100, Math.round((Number(b.value) || 0) / (b.max || max) * 100)));
                return (
                    <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography sx={{ fontSize: 11, color: 'var(--muted)', width: 90, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.label}</Typography>
                        <Box sx={{ flex: 1, height: '7px', background: 'var(--bar-track)', borderRadius: '3px', overflow: 'hidden' }}>
                            <Box sx={{ height: '7px', borderRadius: '3px', width: `${wpct}%`, background: 'var(--teal)' }} />
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--dim)', minWidth: 28, textAlign: 'right', flexShrink: 0 }}>{b.value}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

function BlockTable({ block }) {
    const rows = block.rows.slice(0, PREVIEW_ROWS);
    return (
        <Box sx={{ fontFamily: 'system-ui' }}>
            {/* tableLayout:'fixed' divides width evenly across columns regardless
                of content, so short cells no longer collapse their column; each
                cell keeps an ellipsis so long values still truncate cleanly. */}
            <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 10 }}>
                <Box component="thead">
                    <Box component="tr">
                        {block.columns.map((c) => (
                            <Box key={c} component="th" sx={{ textAlign: 'left', fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.disabled', borderBottom: '2px solid', borderColor: 'divider', px: 0.75, py: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map((r, i) => (
                        <Box component="tr" key={i}>
                            {r.map((cell, j) => (
                                <Box key={j} component="td" sx={{ px: 0.75, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', color: j === 0 ? 'text.primary' : 'text.secondary', fontWeight: j === 0 ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell}</Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
            {block.total > rows.length && (
                <Typography sx={{ fontSize: 9, color: 'text.disabled', mt: 0.5, fontStyle: 'italic' }}>Showing {rows.length} of {block.total} rows</Typography>
            )}
        </Box>
    );
}

function BlockNarrative({ block }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {block.paragraphs.map((p, i) => (
                <Box key={i}>
                    {p.h && <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{p.h}</Typography>}
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.5 }}>{p.body}</Typography>
                    {p.action && <Typography sx={{ fontSize: 9.5, color: 'secondary.main', fontStyle: 'italic' }}>→ {p.action}</Typography>}
                </Box>
            ))}
        </Box>
    );
}

export function PreviewBlock({ block }) {
    if (!block) return null;
    if (block.kind === 'gauge')     return <BlockGauge block={block} />;
    if (block.kind === 'metrics')   return <BlockMetrics block={block} />;
    if (block.kind === 'bars')      return <BlockBars block={block} />;
    if (block.kind === 'table')     return <BlockTable block={block} />;
    return <BlockNarrative block={block} />;
}

// -- Format previews --------------------------------------------------------
function PdfPreview({ title, sections }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 620, bgcolor: 'background.paper', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 4 }}>
            <Box sx={{ borderBottom: '2px solid', borderColor: 'text.primary', pb: 1.25, mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>{title || 'Untitled report'}</Typography>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Boxphish</Typography>
            </Box>
            {sections.map((s, i) => (
                <Box key={s.id} sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Section {i + 1} · {s.group}{s.withDrill ? ' · with drill-down' : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 0.75 }}>{s.label}</Typography>
                    <PreviewBlock block={s.block} />
                </Box>
            ))}
            <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>Confidential · Page 1</Typography>
        </Box>
    );
}

function PowerPointPreview({ title, sections }) {
    const slides = [{ id: 'cover', label: title || 'Untitled report', cover: true }, ...sections];
    return (
        <Box sx={{ width: '100%', maxWidth: 780, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
            {slides.map((s, i) => (
                <Box key={s.id || i} sx={{ aspectRatio: '16 / 9', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <Typography sx={{ fontSize: 8, color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {s.cover ? 'Cover' : `Slide ${i} · ${s.group}`}
                    </Typography>
                    {s.cover ? (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary' }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 9, color: 'text.secondary', mt: 0.5 }}>Boxphish</Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', mt: 0.25, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.label}>{s.label}</Typography>
                            <Box sx={{ flex: 1, mt: 0.75, overflow: 'hidden', transform: 'scale(0.92)', transformOrigin: 'top left' }}>
                                <PreviewBlock block={s.block} />
                            </Box>
                        </>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function CsvPreview({ title, sections }) {
    const text = generateCsv(title, sections);
    const lines = text.split('\n').slice(0, 28);
    return (
        <Box sx={{ width: '100%', maxWidth: 720, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', fontFamily: 'monospace' }}>{(title || 'export').replace(/\s+/g, '-').toLowerCase()}.csv</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{sections.length} blocks</Typography>
            </Box>
            <Box sx={{ p: 2, overflowX: 'auto' }}>
                {lines.map((l, i) => (
                    <Typography key={i} sx={{ fontFamily: 'monospace', fontSize: 10.5, color: l.startsWith('##') ? 'secondary.main' : l.startsWith('#') ? 'text.disabled' : 'text.secondary', fontWeight: l.startsWith('##') ? 700 : 400, whiteSpace: 'nowrap', lineHeight: 1.6 }}>{l || ' '}</Typography>
                ))}
                {text.split('\n').length > 28 && <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, color: 'text.disabled' }}>...</Typography>}
            </Box>
        </Box>
    );
}

function ExcelPreview({ title, sections }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 720, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{(title || 'export').replace(/\s+/g, '-').toLowerCase()}.xlsx</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{sections.length} sheet{sections.length === 1 ? '' : 's'}</Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 460, overflowY: 'auto' }}>
                {sections.map((s) => (
                    <Box key={s.id}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: '4px 4px 0 0' }}>{s.label}</Typography>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '0 0 4px 4px', p: 1 }}>
                            <PreviewBlock block={s.block} />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default function ExportPreview({ format, title, sections }) {
    if (sections.length === 0) {
        return (
            <Box sx={{ width: '100%', maxWidth: 720, p: 6, bgcolor: 'background.paper', border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Select components on the left to see a live preview of the data.</Typography>
            </Box>
        );
    }
    if (format === 'PDF')        return <PdfPreview title={title} sections={sections} />;
    if (format === 'PowerPoint') return <PowerPointPreview title={title} sections={sections} />;
    if (format === 'CSV')        return <CsvPreview title={title} sections={sections} />;
    return <ExcelPreview title={title} sections={sections} />;
}
